<template>
    <div class="expediente-root">

        <!-- Header del expediente -->
        <div class="exp-header">
            <div class="row items-center q-gutter-x-md">
                <div class="exp-avatar">
                    <q-icon name="mdi-account" size="32px" color="white" />
                </div>
                <div class="column">
                    <span class="exp-nombre">
                        {{ paciente?.nombres }} {{ paciente?.apellidoPaterno }} {{ paciente?.apellidoMaterno }}
                    </span>
                </div>
            </div>
            <q-space />
            <div class="row items-center q-gutter-x-sm text-grey-6" style="font-size:13px">
                <q-icon name="mdi-calendar" size="16px" />
                <span>{{ fechaHoy }}</span>
            </div>
        </div>

        <!-- Cuerpo principal -->
        <div class="exp-body">

            <!-- Sidebar de documentos -->
            <div class="exp-sidebar">
                <div class="sidebar-title">Documentos</div>

                <div v-for="item in menuItems" :key="item.name" class="sidebar-item"
                    :class="{ 'sidebar-item--active': tab === item.name }" @click="tab = item.name">
                    <div class="sidebar-item-icon">
                        <q-icon :name="item.icon" size="20px" />
                    </div>
                    <div class="column">
                        <span class="sidebar-item-label">{{ item.label }}</span>
                        <span class="sidebar-item-sub">{{ item.sub }}</span>
                    </div>
                    <q-space />
                    <q-icon name="mdi-chevron-right" size="18px" class="sidebar-chevron"
                        :class="{ 'sidebar-chevron--active': tab === item.name }" />
                </div>
            </div>

            <!-- Panel de contenido -->
            <div class="exp-content">
                <q-tab-panels v-model="tab" animated transition-prev="fade" transition-next="fade"
                    style="height:100%; background:transparent">
                    <q-tab-panel style="padding:0; height:100%" name="AVISO">
                        <AvisoPrivacidad />
                    </q-tab-panel>
                    <q-tab-panel style="padding:0; height:100%" name="mails">
                        <EstudioSocioEconomico />
                    </q-tab-panel>
                    <q-tab-panel style="padding:0; height:100%" name="Nota">
                        <NotaMedica />
                    </q-tab-panel>
                    <q-tab-panel style="padding:0; height:100%" name="Visita">
                        <VisitaDomiciliaria />
                    </q-tab-panel>
                    <q-tab-panel style="padding:0; height:100%" name="Psicología">
                        <ConsultaPsicologica />
                    </q-tab-panel>
                    <q-tab-panel style="padding:0; height:100%" name="Fisioterapia">
                        <ConsultaFisioterapia />
                    </q-tab-panel>
                </q-tab-panels>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import EstudioSocioEconomico from './EstudioSocioEconomico.vue'
import AvisoPrivacidad from './AvisoPrivacidad.vue'
import VisitaDomiciliaria from './VisitaDomiciliaria.vue'
import NotaMedica from './NotaMedica.vue'
import ConsultaPsicologica from './ConsultaPsicologica.vue'
import ConsultaFisioterapia from './ConsultaFisioterapia.vue'

const store = useStore()
const tab = ref('AVISO')

const paciente = computed(() => store.state.pacienteStore)

const fechaHoy = new Date().toLocaleDateString('es-MX', {
    day: '2-digit', month: 'long', year: 'numeric'
})

const menuItems = [
    { name: 'AVISO', icon: 'mdi-shield-lock', label: 'Aviso de Privacidad', sub: 'Consentimiento informado' },
    { name: 'mails', icon: 'mdi-home-account', label: 'Estudio Socioeconómico', sub: 'Perfil socioeconómico' },
    { name: 'Nota', icon: 'mdi-stethoscope', label: 'Nota Médica', sub: 'Registro clínico' },
    { name: 'Visita', icon: 'mdi-home-map-marker', label: 'Visita Domiciliaria', sub: 'Seguimiento en hogar' },
    { name: 'Psicología', icon: 'mdi-head-heart', label: 'Hoja Clínica Psicología', sub: 'Evaluación psicológica' },
    { name: 'Fisioterapia', icon: 'mdi-human-handsup', label: 'Hoja Clínica Fisioterapia', sub: 'Rehabilitación física' },
]
</script>

<style scoped>
/* ── Layout raíz ─────────────────────────────────── */
.expediente-root {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f4f6f9;
    overflow: hidden;
}

/* ── Header ──────────────────────────────────────── */
.exp-header {
    display: flex;
    align-items: center;
    padding: 16px 24px;
    background: #ffffff;
    border-bottom: 1px solid #e3e8ef;
    box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
    flex-shrink: 0;
}

.exp-avatar {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, #1565c0, #42a5f5);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(21, 101, 192, .3);
}

.exp-nombre {
    font-size: 17px;
    font-weight: 700;
    color: #1a2332;
    line-height: 1.3;
}

.exp-sub {
    font-size: 12px;
    color: #8a96a8;
    margin-top: 2px;
}

/* ── Cuerpo ──────────────────────────────────────── */
.exp-body {
    display: flex;
    flex: 1;
    overflow: hidden;
}

/* ── Sidebar ─────────────────────────────────────── */
.exp-sidebar {
    width: 260px;
    flex-shrink: 0;
    background: #ffffff;
    border-right: 1px solid #e3e8ef;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding: 16px 12px;
}

.sidebar-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.2px;
    color: #a0aab8;
    text-transform: uppercase;
    padding: 0 8px 12px;
}

.sidebar-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 10px;
    border-radius: 10px;
    cursor: pointer;
    margin-bottom: 4px;
    transition: background .15s, box-shadow .15s;
}

.sidebar-item:hover {
    background: #f0f4ff;
}

.sidebar-item--active {
    background: #e8f0fe;
    box-shadow: inset 3px 0 0 #1565c0;
}

.sidebar-item-icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #f0f4ff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1565c0;
    flex-shrink: 0;
    transition: background .15s;
}

.sidebar-item--active .sidebar-item-icon {
    background: #1565c0;
    color: #ffffff;
}

.sidebar-item-label {
    font-size: 13px;
    font-weight: 600;
    color: #1a2332;
    line-height: 1.3;
}

.sidebar-item-sub {
    font-size: 11px;
    color: #a0aab8;
    line-height: 1.2;
}

.sidebar-item--active .sidebar-item-label {
    color: #1565c0;
}

.sidebar-chevron {
    color: #d0d7e2;
    transition: transform .2s, color .15s;
}

.sidebar-chevron--active {
    color: #1565c0;
    transform: rotate(90deg);
}

/* ── Panel de contenido ──────────────────────────── */
.exp-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}
</style>